import { ELogLevel } from '../../../shared/constants';
import { ILogEntry } from '../../types';
import { logLevelToString } from '../sentry';

export interface IConsoleIntegrationOptions {
	enableColors?: boolean;
	enableTimestamps?: boolean;
}

/**
 * Console integration for logger
 * Outputs log entries to console with formatting
 */
export class ConsoleIntegration {
	private enableColors: boolean;
	private enableTimestamps: boolean;

	constructor(options: IConsoleIntegrationOptions) {
		this.enableColors = options.enableColors ?? true;
		this.enableTimestamps = options.enableTimestamps ?? true;
	}

	/**
	 * Log entry to console
	 * @param entry - Log entry to output
	 */
	public log(entry: ILogEntry): void {
		const parts: string[] = [];

		if (this.enableTimestamps) {
			parts.push(`[${entry.timestamp.toISOString()}]`);
		}

		const levelBadge = this.formatLevel(entry.level);
		parts.push(levelBadge);

		if (entry.event) {
			parts.push(`[${entry.event}]`);
		}

		if (entry.namespace) {
			parts.push(`[${entry.namespace}]`);
		}

		parts.push(entry.message);

		const consoleMethod = this.getConsoleMethod(entry.level);
		consoleMethod(parts.join(' '));

		const hasMetadata =
			entry.metadata && Object.keys(entry.metadata).length > 0;
		const hasContext = entry.context && Object.keys(entry.context).length > 0;

		if (hasMetadata || hasContext) {
			/** Use collapsed group with console.info to avoid stack traces */
			console.groupCollapsed('Details');

			if (hasMetadata) {
				console.info('Metadata:', entry.metadata);
			}

			if (hasContext) {
				console.info('Context:', entry.context);
			}

			console.groupEnd();
		}
	}

	/**
	 * Format log level badge with optional colors
	 * @param level - Log level
	 * @returns Formatted level string
	 */
	private formatLevel(level: ELogLevel): string {
		const label = logLevelToString(level);

		if (!this.enableColors) {
			return `[${label}]`;
		}

		const colors: Record<ELogLevel, string> = {
			[ELogLevel.DEBUG]: '\x1b[36m', // Cyan
			[ELogLevel.INFO]: '\x1b[32m', // Green
			[ELogLevel.WARN]: '\x1b[33m', // Yellow
			[ELogLevel.ERROR]: '\x1b[31m', // Red
		};
		const reset = '\x1b[0m';

		return `${colors[level]}[${label}]${reset}`;
	}

	/**
	 * Get appropriate console method for log level
	 * @param level - Log level
	 * @returns Console method
	 */
	private getConsoleMethod(level: ELogLevel): typeof console.log {
		switch (level) {
			case ELogLevel.DEBUG:
				return console.log;
			case ELogLevel.INFO:
				return console.info;
			case ELogLevel.WARN:
				return console.warn;
			case ELogLevel.ERROR:
				return console.error;
			default:
				return console.log;
		}
	}
}
